import moment, { Moment } from 'moment'
import React from 'react'

import { EuiFlexGroup, EuiFlexItem, EuiPanel, EuiText, EuiTitle } from '@elastic/eui'
import '@elastic/eui/dist/eui_theme_light.css'

import { MasterCampaignPlanCampaignBuilderType } from 'api/entities/MasterCampaignStub'

interface ChartItem {
  name: string
  type: MasterCampaignPlanCampaignBuilderType
  startDate: Moment
  endDate: Moment
}

interface GanttChartProps {
  chartItems: ChartItem[]
}

const GanttChartOld: React.FC<GanttChartProps> = ({ chartItems }) => {
  const allDates = chartItems.flatMap(c => [c.startDate, c.endDate])
  const minDate = moment.min(allDates).startOf('day')
  const maxDate = moment.max(allDates).endOf('day')
  const totalDays = maxDate.diff(minDate, 'days') + 1

  const months: Moment[] = []
  let currentMonth = minDate.clone().startOf('month')
  while (currentMonth.isSameOrBefore(maxDate, 'month')) {
    months.push(currentMonth.clone())
    currentMonth.add(1, 'month')
  }

  const typeColors: Partial<Record<MasterCampaignPlanCampaignBuilderType, string>> = {
    Internet: '#6B7F5B',
    TV: '#8B5A3E',
    Billboard: '#C49A4D',
    OffAdCritter: '#999999'
  }

  const sortedCampaigns = [...chartItems].sort((a, b) => a.startDate.diff(b.startDate))
  const rows: ChartItem[][] = []
  for (const campaign of sortedCampaigns) {
    let placed = false
    for (const row of rows) {
      const hasOverlap = row.some(existing => campaign.startDate.isSameOrBefore(existing.endDate) && campaign.endDate.isSameOrAfter(existing.startDate))
      if (!hasOverlap) {
        row.push(campaign)
        placed = true
        break
      }
    }
    if (!placed) {
      rows.push([campaign])
    }
  }

  const getTypeColor = (type: MasterCampaignPlanCampaignBuilderType) => typeColors[type] || '#999999'

  return (
    <EuiPanel paddingSize='m' style={{ maxHeight: '350px', overflow: 'hidden', paddingTop: 0 }}>
      <EuiTitle size='s'>
        <h3>Campaign Timeline</h3>
      </EuiTitle>
      <EuiFlexGroup gutterSize='m' style={{ marginBottom: 8 }}>
        <EuiFlexItem grow={true} />
        {Object.entries(typeColors).map(([type, color]) => (
          <EuiFlexItem key={type} grow={false}>
            <EuiFlexGroup alignItems='center' gutterSize='xs'>
              <div
                style={{
                  width: 16,
                  height: 16,
                  background: color,
                  borderRadius: 4,
                  marginRight: '2px'
                }}
              />
              <EuiText size='s'>{type}</EuiText>
            </EuiFlexGroup>
          </EuiFlexItem>
        ))}
      </EuiFlexGroup>
      <div style={{ maxHeight: '230px', paddingRight: 8 }}>
        <EuiFlexGroup direction='column' gutterSize='s'>
          {rows.map((row, rowIndex) => (
            <EuiFlexItem key={rowIndex}>
              <div
                style={{
                  position: 'relative',
                  height: 20,
                  background: '#F5F7FA',
                  width: '100%',
                  boxSizing: 'border-box'
                }}>
                {row.map((campaign, campaignIndex) => {
                  const startOffset = campaign.startDate.diff(minDate, 'days')
                  const duration = campaign.endDate.diff(campaign.startDate, 'days') + 1 // Include end day
                  const startPercentage = (startOffset / totalDays) * 100
                  const widthPercentage = (duration / totalDays) * 100

                  return (
                    <div
                      key={campaignIndex}
                      style={{
                        position: 'absolute',
                        left: `${startPercentage}%`,
                        width: `${widthPercentage}%`,
                        height: '100%',
                        background: getTypeColor(campaign.type),
                        borderRadius: 2
                      }}
                      title={`${campaign.name}: ${campaign.startDate.format('L')} - ${campaign.endDate.format('L')}`}
                    />
                  )
                })}
              </div>
            </EuiFlexItem>
          ))}
        </EuiFlexGroup>
      </div>
      <EuiFlexGroup direction='column' style={{ marginTop: 8 }}>
        <EuiFlexItem>
          <div style={{ position: 'relative', height: 20, width: '100%', boxSizing: 'border-box' }}>
            {months.map((month, index) => {
              const monthOffset = month.diff(minDate, 'days')
              const monthPercentage = (monthOffset / totalDays) * 100
              return (
                <div
                  key={index}
                  style={{
                    position: 'absolute',
                    left: `${monthPercentage}%`,
                    height: '100%',
                    borderLeft: '1px solid #D3DAE6',
                    paddingLeft: 4,
                    whiteSpace: 'nowrap'
                  }}>
                  <EuiText size='xs'>{month.format('MMM')}</EuiText>
                </div>
              )
            })}
          </div>
        </EuiFlexItem>
      </EuiFlexGroup>
    </EuiPanel>
  )
}

export default GanttChartOld
